/**
 * 2D table geometry contract.
 *
 * Everything is authored against a fixed design resolution and scaled to fit
 * the viewport, so layout maths never has to care about the real window size.
 * Origin is top-left, +y is down — Pixi's convention, not Three's.
 *
 * ## Two orientations
 *
 * The table was authored landscape, 1920x1080, with the stock on the left and
 * four piles in a row to the right of it. Letterboxed into a portrait phone
 * that shape collapses: a 16:9 rect contain-fitted into 390x844 renders 390x219
 * — a fifth of the screen, with 42px cards. Unplayable, and not fixable by
 * scaling, because the problem is the aspect ratio itself.
 *
 * So there are two **profiles**. Landscape is exactly what it always was, to
 * the pixel. Portrait is a taller rect with the four piles in a 2x2 grid and
 * the stock beneath them, where a thumb can reach it.
 *
 * ## What varies and what does not — read this before adding a profile field
 *
 * Only the **arrangement** varies: the design rect, where the piles sit, how
 * far a pile fans, and where the stock and discard are.
 *
 * `CARD`, `LIGHT`, `WELL`, `TABLE`, `SHADOW` and `TOTAL_BADGE` are deliberately
 * invariant. That is not tidiness, it is what keeps this change small: those
 * values are **baked as GLSL constants** at module load in `cardLighting`,
 * `foil`, `atmosphere` and `table`, and into the card atlas by `cardTexture`.
 * Vary one and every one of those shaders needs rebuilding and re-tuning, and
 * the whole "depth is a lie told consistently" contract has to be re-verified.
 * Vary none and the cards, the light and the shadows are literally the same
 * objects in both orientations — only laid out differently.
 *
 * ## Why the profile is chosen in this module's body
 *
 * ES modules evaluate dependencies before dependents, and this module is a
 * dependency of every module that reads a layout value. Choosing the profile
 * here, at import, means the ~200 module-level constants derived from these
 * numbers across 22 files are already correct by the time anything else is
 * evaluated — without any of them having to change.
 *
 * `configureLayout()` exists for a *later* change of orientation, which is a
 * different and much smaller problem: see `relayout()` in `@/main`.
 */

import { PILE_COUNT } from '@/game/types';

export type Orientation = 'landscape' | 'portrait';

/* ------------------------------------------------------------- invariants -- */

/**
 * Card size in design units.
 *
 * **A true poker-card proportion, 2.5 x 3.5in — h/w exactly 1.40.**
 *
 * This was 200 x 290, which is 1.4500: 3.6% too narrow for its height. A
 * playing card is a shape people know better than almost any other object, so
 * a few percent reads instantly as "slightly off" without being nameable — and
 * because the face art is stretched to fill the card, everything printed on it
 * gets squeezed by the same amount. The suits were the giveaway.
 *
 * The same in both orientations, on purpose — see the note at the top.
 */
export const CARD = {
  width: 207,
  height: 290,
  cornerRadius: 14,
} as const;

/** Small per-card lateral jitter so stacks look hand-dealt. */
export const PILE_JITTER = 3;

/** A pile never exceeds this — the fifth card always resolves it. */
export const PILE_MAX_CARDS = 5;

/**
 * The pile well: the recess a pile sits in.
 *
 * Lives here rather than in `@/pixi/table` because it is geometry, and because
 * the total badges have to be placed relative to it — see `TOTAL_BADGE.offsetY`.
 * `table.ts` imports these for its shader constants.
 */
export const WELL = {
  /** How far the recess extends past the card on every side. */
  margin: 10,
  /**
   * Falloff constant of the well's glow onto the surrounding felt, in design
   * units. The shader uses `exp(-d / bleed)`, so the glow is at 37% one of
   * these out from the well edge, 13% at two, and 5% at three.
   */
  bleed: 26,
} as const;

/** Half-height of a well, from the pile centre to its top edge. */
const WELL_HALF_Y = CARD.height / 2 + WELL.margin;

/**
 * Clearance between the bottom of a total badge and the top of its well.
 *
 * Sized in units of the glow's falloff, not by eye: at 1.7 bleed constants the
 * glow is down to roughly 18% where the badge starts, which reads as separated
 * rather than as the badge sitting in the halo.
 */
const TOTAL_BADGE_CLEARANCE = Math.round(WELL.bleed * 1.7);

/** Total readouts sit above each pile, clear of the downward fan and the well glow. */
export const TOTAL_BADGE = {
  width: 150,
  height: 84,
  /** Negative: the badge centre sits this far above the pile's first card. */
  offsetY: -(WELL_HALF_Y + TOTAL_BADGE_CLEARANCE + 84 / 2),
} as const;

/** How far a badge reaches above its pile's centre — the row's true top edge. */
const PILE_REACH_UP = -TOTAL_BADGE.offsetY + TOTAL_BADGE.height / 2;

/**
 * The virtual light.
 *
 * There is no real 3D here, so depth is a lie told convincingly: every card
 * carries a generated normal map, and this light position drives the per-pixel
 * shading in the card shader plus the direction and length of every drop
 * shadow. Keep them consistent — a shadow that disagrees with the shading is
 * the fastest way to make a 2D scene look flat and wrong.
 *
 * Invariant across orientations. Consistency is what sells the depth, and this
 * light is consistent with itself wherever the piles happen to be; re-aiming it
 * per profile would mean rebuilding every shader that bakes it for no gain.
 */
export const LIGHT = {
  /** Position in design units. Above and to the left, raking across the table. */
  x: 620,
  y: -240,
  /** Virtual height above the table plane. Larger = softer, more parallel light. */
  z: 900,
  /** Warm key. */
  colour: 0xffe9c4,
  intensity: 1.0,
  /** Cool bounce from the opposite side so shadowed edges never go dead black. */
  fill: { x: 1500, y: 400, z: 520, colour: 0x5b6ff0, intensity: 0.32 },
  ambient: 0.34,
} as const;

/** Drop-shadow tuning, derived from LIGHT so shadows and shading agree. */
export const SHADOW = {
  /** Multiplier converting a card's virtual lift into shadow offset. */
  offsetScale: 0.28,
  /** Blur grows with lift — a card in the air casts a softer shadow. */
  blurBase: 8,
  blurPerLift: 0.42,
  alphaBase: 0.5,
} as const;

/**
 * The table itself.
 *
 * `railThickness` is the one value here that is not fixed. It is framing, and
 * on a phone framing is charged for twice — once down each side — out of the
 * width the cards are competing for. At the authored 46 it costs 92 design
 * units, which on a portrait phone is 12% of the entire width budget and, once
 * the stock moved into the middle of the grid, was the single thing standing
 * between the cards and another 8%. A phone gets a 16-unit rail: still a lip
 * that reads as the edge of a table, just not a mahogany one.
 *
 * Safe to vary only because `buildFragment` bakes it per table construction and
 * the table is rebuilt whenever the layout moves — unlike `CARD`, `LIGHT` and
 * `WELL`, which are baked into the card atlas and cannot follow.
 */
export const TABLE = {
  /** Inset of the felt from the design bounds. */
  inset: 0,
  railThickness: 46,
  /** Radius of the felt's rounded corners. */
  radius: 54,
};

const AUTHORED_RAIL = 46;
const COMPACT_RAIL = 16;

/**
 * Clear space either side of the stock, beyond the two cards' own half-widths.
 *
 * A card *in flight* is drawn larger — the shader turns lift into a real
 * perspective divide — and will cross this gutter on its way to a pile. That is
 * correct: a thrown card passes over the table. What must never happen is two
 * cards at rest overlapping, so this is measured against resting cards plus the
 * lateral jitter a fifth card can take.
 */
const STOCK_GUTTER = 20;

/* ---------------------------------------------------------------- profiles -- */

interface Profile {
  readonly orientation: Orientation;
  readonly design: { width: number; height: number };
  /** Columns in the pile grid. 4 is a row; 2 is a 2x2. */
  readonly columns: number;
  /** Horizontal distance between pile centres. */
  readonly spacing: number;
  /** Vertical distance between pile rows. Unused when there is only one row. */
  readonly rowSpacing: number;
  /** Horizontal centre of the pile block. */
  readonly centreX: number;
  /** Centre of the FIRST card of the first pile row. */
  readonly y: number;
  /** Each subsequent card in a pile steps down by this much. */
  readonly fan: number;
  /**
   * Where the stock sits.
   *
   * `beside` is an explicit position, used by landscape where the stock has a
   * column of its own to the left of the piles. `centre` puts it in the middle
   * of the pile grid — see the note on the portrait profile.
   */
  readonly stock: { at: 'beside'; x: number; y: number } | { at: 'centre' };
}

/**
 * The original table, unchanged.
 *
 * The play area sits to the RIGHT of the stock rather than below it. Piles grow
 * downward and hold at most 5 cards, so a full pile is a known
 * `4 * fan + CARD.height` tall. With the stock underneath the piles that stack
 * collided with it from the fourth card on, while 37% of the horizontal field
 * sat empty. Moving the stock to the left edge spends that dead width to buy
 * the vertical room, and reads left-to-right: here is the card, put it there.
 */
const LANDSCAPE: Profile = {
  orientation: 'landscape',
  design: { width: 1920, height: 1080 },
  columns: 4,
  spacing: 345,
  rowSpacing: 0,
  centreX: 1140,
  /**
   * Kept low enough that the total badges clear the HUD's top bar. The HUD is
   * DOM measured in screen pixels while this field scales with the viewport, so
   * on a short window the badges rise toward a top bar that does not move. The
   * margin here is sized for the worst case, not the 1080-tall one.
   */
  y: 490,
  fan: 56,
  /** Face-up, on the left edge, centred against the play area (345..859). */
  stock: { at: 'beside', x: 250, y: 602 },
};

/**
 * Portrait: a 2x2 grid with the stock in the middle of it.
 *
 * ### Why the stock is in the centre and not underneath
 *
 * Portrait is height-starved in a way landscape is not. Two rows of five-card
 * fans have to stack inside one screen and the DOM HUD eats both ends of it, so
 * the card size is set by the vertical budget alone — every unit of height is a
 * unit of card. Measured on a 390x844 phone, the play filled 98% of the band it
 * had. There was nothing left to reclaim by tidying.
 *
 * A stock beneath the grid costs 310 of those units, a fifth of the whole
 * budget, to show one card. Meanwhile the middle of the grid — a channel 227
 * units wide running the full height between the two columns — was empty felt.
 * Moving the stock into that channel spends width the layout was wasting to buy
 * height it could not get any other way, and the cards go from 97 CSS px to 114
 * on that phone. Nothing else in this layout was worth 17%.
 *
 * It is also simply where the stock belongs. The player never touches it —
 * `installPointer` hit-tests piles and nothing else, so the stock is a thing to
 * read rather than a thing to press — and the card being read is now at the
 * centre of the screen, equidistant from the four piles it can go to, instead
 * of at the bottom edge in the dark part of the vignette.
 *
 * ### What sets the numbers
 *
 * `spacing` is the one that matters: it has to clear a stock card between the
 * two columns, and because portrait is now *width*-bound, every unit added here
 * comes straight off the card. Half of it has to hold, from the pile's centre
 * outward: half a pile card (103.5), the lateral jitter a fifth card can take
 * (6), a gutter you can see (`STOCK_GUTTER`), and half a stock card (103.5).
 *
 * `rowSpacing` is no longer fighting for room. A row needs `PILE_REACH_UP` (283)
 * above its centre and `4 * fan + CARD.height / 2 + WELL.margin` (323) below it,
 * so 606 is the floor; 686 spends some of the height the stock gave back on a
 * gutter between the rows. The fan stays at the landscape-quality 42 rather than
 * being cut to the 41 the corner index demands.
 */
const PORTRAIT: Profile = {
  orientation: 'portrait',
  design: { width: 860, height: 1900 },
  columns: 2,
  /** 2 * (103.5 pile card + 6 jitter + STOCK_GUTTER + 103.5 stock card). */
  spacing: 2 * (CARD.width / 2 + PILE_JITTER * 2 + STOCK_GUTTER + CARD.width / 2),
  rowSpacing: 686,
  centreX: 430,
  y: 448,
  fan: 42,
  stock: { at: 'centre' },
};

/* ------------------------------------------------------------------- state -- */

/**
 * Live geometry.
 *
 * These are exported as **mutable objects rather than consts** so that a change
 * of orientation can rewrite them in place and every holder of the reference
 * sees the new value. Read them, never write them — `configureLayout` owns them.
 */

/** Authoring resolution. All positions below are in these units. */
export const DESIGN = { width: 0, height: 0 };

/** Where the piles sit and how they fan. */
export const PILES = {
  columns: 0,
  spacing: 0,
  rowSpacing: 0,
  centreX: 0,
  y: 0,
  fan: 0,
};

/** The face-up stock the player draws from. */
export const STOCK = { x: 0, y: 0 };

/** Where cleared and busted piles fly to before being recycled. */
export const DISCARD = { x: 0, y: 0 };

let current: Profile = LANDSCAPE;

export function orientation(): Orientation {
  return current.orientation;
}

function apply(profile: Profile): void {
  current = profile;
  TABLE.railThickness = AUTHORED_RAIL;

  DESIGN.width = profile.design.width;
  DESIGN.height = profile.design.height;

  PILES.columns = profile.columns;
  PILES.spacing = profile.spacing;
  PILES.rowSpacing = profile.rowSpacing;
  PILES.centreX = profile.centreX;
  PILES.y = profile.y;
  PILES.fan = profile.fan;

  if (profile.stock.at === 'beside') {
    STOCK.x = profile.stock.x;
    STOCK.y = profile.stock.y;
  } else {
    // Dead centre of the grid: the channel between the columns, halfway down
    // between the rows.
    const rows = Math.ceil(PILE_COUNT / profile.columns);
    STOCK.x = profile.centreX;
    STOCK.y = profile.y + ((rows - 1) * profile.rowSpacing) / 2;
  }

  // Off the right edge, far enough out that a card is fully clear of the felt
  // before it is recycled.
  DISCARD.x = profile.design.width + 260;
  DISCARD.y = 260;
}

/* ------------------------------------------------------- filling the screen -- */

/**
 * How much of the viewport the DOM HUD has claimed, in CSS pixels.
 *
 * The HUD is not part of the design space — it is DOM, laid out in screen
 * pixels — so the felt has to be told how much room it has left. The caller
 * measures the real elements rather than this module guessing from the CSS,
 * because the CSS uses `clamp()` against both `vw` and `vh` and the safe-area
 * insets are only known to the browser.
 */
export interface HudInsets {
  readonly top: number;
  readonly bottom: number;
}

/** Used until the HUD exists to be measured. Close to what the CSS produces. */
const DEFAULT_INSETS: HudInsets = { top: 72, bottom: 50 };

let insets: HudInsets = DEFAULT_INSETS;

/**
 * Below this, in CSS pixels on the viewport's *shorter* edge, a screen is small
 * enough that the authored framing is a luxury and the cards matter more.
 *
 * 540 catches every phone in both orientations, and a genuinely small desktop
 * window — which has exactly the same problem and deserves the same answer.
 */
const COMPACT_MAX_EDGE = 540;

/**
 * The same, for touch devices, which are all worth filling however big they get.
 *
 * A tablet is nowhere near the 540 line but still wastes real estate on the
 * authored rect: an iPad held upright fits the portrait rect against its width
 * and leaves 152 CSS px of bar down each side. It is a mobile screen and should
 * be filled like one.
 *
 * 1024 is where this stops, which is the long edge of an iPad Pro in portrait
 * and comfortably under the 1080 of a touchscreen laptop — those have a desktop
 * amount of room and should keep the desktop framing.
 */
const COMPACT_MAX_TOUCH_EDGE = 1024;

/** Whether the primary input is a finger. Non-browser callers get `false`. */
export function coarsePointer(): boolean {
  const mm = globalThis.matchMedia?.bind(globalThis);
  if (typeof mm !== 'function') return false;
  try {
    return mm('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}

/** Breathing room kept between the content and the viewport edge, in CSS px. */
const EDGE_MARGIN = 8;

/** The bounding box of everything that must stay on screen, in design units. */
interface Extent {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * Where the play actually reaches — badges, full fans, wells and the stock.
 *
 * Derived from the live layout rather than authored, so it stays true whatever
 * profile is in force and whatever the fan is tuned to.
 */
function contentExtent(): Extent {
  const halfWidth = CARD.width / 2 + WELL.margin;
  const spread = ((PILES.columns - 1) / 2) * PILES.spacing;

  return {
    top: Math.min(PILES.y - PILE_REACH_UP, STOCK.y - WELL_HALF_Y),
    bottom: Math.max(playBottom(), STOCK.y + WELL_HALF_Y),
    left: Math.min(PILES.centreX - spread - halfWidth, STOCK.x - halfWidth),
    right: Math.max(PILES.centreX + spread + halfWidth, STOCK.x + halfWidth),
  };
}

/**
 * Resize the design rect so the play fills a small screen.
 *
 * On a phone the authored rect wastes room in two different ways. Landscape
 * wastes it sideways: a 16:9 rect contain-fitted into an 844x390 phone leaves
 * 75 CSS px of black bar down each side, 18% of the screen, because the rect is
 * narrower than the phone. And it wastes it downward: the play is 662 design
 * units tall inside a 1080-unit rect, so 39% of the height is framing that a
 * phone cannot afford.
 *
 * Both have the same fix. Make the rect the *viewport's* shape, so there are no
 * bars by construction, and make it only as large as the content needs, so the
 * scale — and with it every card — comes up to meet the screen. The authored
 * arrangement is untouched: spacing, fan and the stock's relationship to the
 * piles are all preserved exactly, and the whole block is translated as one.
 *
 * Cards go from 75 to 92 CSS px on a landscape phone and 92 to 100 in portrait.
 * Portrait gains less because it was already nearly tight — see the note on the
 * portrait profile. The limit there is real: two rows of five-card fans plus a
 * stock is a tall stack, and a card cannot be widened without being heightened.
 */
function fitToViewport(viewportWidth: number, viewportHeight: number): void {
  TABLE.railThickness = COMPACT_RAIL;

  const box = contentExtent();
  const contentWidth = box.right - box.left;
  const contentHeight = box.bottom - box.top;
  const rail = TABLE.inset + TABLE.railThickness;

  // Two independent constraints, not one. The play has to clear the HUD, which
  // is measured in CSS pixels and does not scale; and it has to stay on the
  // felt rather than sliding under the rail, which is measured in design units
  // and scales with everything else. Adding them together double-counts —
  // vertically the HUD already pushes the content well inside the rail — so
  // each gives its own candidate scale and the tighter one wins.
  const byHudX = (viewportWidth - EDGE_MARGIN * 2) / contentWidth;
  const byRailX = viewportWidth / (contentWidth + rail * 2);
  const byHudY =
    (viewportHeight - insets.top - insets.bottom - EDGE_MARGIN * 2) / contentHeight;
  const byRailY = viewportHeight / (contentHeight + rail * 2);

  const scale = Math.min(byHudX, byRailX, byHudY, byRailY);
  if (!(scale > 0) || !Number.isFinite(scale)) return;

  // Quantised so that dragging a window edge does not rebuild the table on
  // every single pixel of travel. Rounded *up*: a rect rounded down is smaller
  // than the scale was solved for, which spends the rail's clearance.
  const width = Math.ceil(viewportWidth / scale / 4) * 4;
  const height = Math.ceil(viewportHeight / scale / 4) * 4;

  // Place it between whichever edges actually bind, in design units.
  const railUnits = rail;
  const top = Math.max((insets.top + EDGE_MARGIN) / scale, railUnits);
  const bottom = height - Math.max((insets.bottom + EDGE_MARGIN) / scale, railUnits);

  const dx = (width - contentWidth) / 2 - box.left;
  const dy = top + (bottom - top - contentHeight) / 2 - box.top;

  DESIGN.width = width;
  DESIGN.height = height;
  PILES.centreX += dx;
  PILES.y += dy;
  STOCK.x += dx;
  STOCK.y += dy;
  DISCARD.x = width + 260;
}

/** Set the room the DOM HUD has taken. Returns `true` if it changed. */
export function setHudInsets(next: HudInsets): boolean {
  if (Math.abs(next.top - insets.top) < 0.5 && Math.abs(next.bottom - insets.bottom) < 0.5) {
    return false;
  }
  insets = { top: next.top, bottom: next.bottom };
  return true;
}

/**
 * Choose and size the layout for a viewport.
 *
 * Portrait is chosen on the viewport's own shape rather than on a device class,
 * because that is the thing that actually matters: a narrow desktop window has
 * the same problem a phone does, and a tablet in landscape has none of it.
 *
 * A small viewport additionally gets the rect resized to fill it — see
 * `fitToViewport`. Anything larger keeps the authored rect exactly, so the
 * desktop game is byte-for-byte what it always was.
 *
 * Returns `true` if anything a built object depends on moved, so the caller can
 * decide whether a rebuild is needed.
 */
export function configureLayout(viewportWidth: number, viewportHeight: number): boolean {
  const before = {
    orientation: current.orientation,
    width: DESIGN.width,
    height: DESIGN.height,
    x: PILES.centreX,
    y: PILES.y,
  };

  const usable = viewportWidth > 0 && viewportHeight > 0;
  apply(usable && viewportHeight > viewportWidth ? PORTRAIT : LANDSCAPE);

  const edge = Math.min(viewportWidth, viewportHeight);
  const fill =
    usable && (edge <= COMPACT_MAX_EDGE || (coarsePointer() && edge <= COMPACT_MAX_TOUCH_EDGE));
  if (fill) fitToViewport(viewportWidth, viewportHeight);

  return (
    current.orientation !== before.orientation ||
    DESIGN.width !== before.width ||
    DESIGN.height !== before.height ||
    PILES.centreX !== before.x ||
    PILES.y !== before.y
  );
}

// Choose before any dependent module is evaluated. See the note at the top.
configureLayout(globalThis.innerWidth ?? 0, globalThis.innerHeight ?? 0);

/* ---------------------------------------------------------------- geometry -- */

export function pilePosition(i: number): { x: number; y: number } {
  const columns = PILES.columns;
  const column = i % columns;
  const row = Math.floor(i / columns);
  const centre = (columns - 1) / 2;
  return {
    x: PILES.centreX + (column - centre) * PILES.spacing,
    y: PILES.y + row * PILES.rowSpacing,
  };
}

export function pileCardPosition(i: number, n: number): { x: number; y: number } {
  const base = pilePosition(i);
  return {
    x: base.x + (n % 2 === 0 ? PILE_JITTER : -PILE_JITTER) * (n * 0.5),
    y: base.y + n * PILES.fan,
  };
}

/**
 * The centre of the block of piles — what the table lamp aims between, and what
 * the badge and popup layers treat as the middle of the action.
 */
export function playCentre(): { x: number; y: number } {
  const rows = Math.ceil(PILE_COUNT / PILES.columns);
  return {
    x: PILES.centreX,
    y: PILES.y + ((rows - 1) * PILES.rowSpacing) / 2,
  };
}

/** How far a pile's total badge reaches above the pile's own centre. */
export function pileReachUp(): number {
  return PILE_REACH_UP;
}

/**
 * The lowest the play ever reaches: the foot of a full bottom-row fan.
 *
 * Derived from the live layout rather than authored, so it stays true whatever
 * profile is in force and whatever the fan is tuned to. `contentExtent` uses it
 * for the fit; the streak callout uses it to stay off the cards.
 */
export function playBottom(): number {
  const rows = Math.ceil(PILE_COUNT / PILES.columns);
  return PILES.y + (rows - 1) * PILES.rowSpacing + belowCentre();
}

/** Reach below a pile's centre: a five-card fan plus the well's own margin. */
function belowCentre(): number {
  return (PILE_MAX_CARDS - 1) * PILES.fan + WELL_HALF_Y;
}
